import { useEffect, useState } from 'react';
import { API_BASE, api, type BoTenant } from '../client/api';
import { ModalShell } from './menu';
import { Button } from './ui';
import type { MediaItem, Notice } from '@cornerstone/shared';

export function IdleContentSettings({ notice, mediaList, isBo, canPublish, portrait, onClose }: {
  notice: Notice | null; mediaList: MediaItem[]; isBo: boolean; canPublish: boolean; portrait: boolean; onClose(): void;
}) {
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [organizations, setOrganizations] = useState<BoTenant[]>([]);
  const [currentOrgId, setCurrentOrgId] = useState('');
  const [targetOrgId, setTargetOrgId] = useState('');
  const [targetNoticeId, setTargetNoticeId] = useState<string | null>(null);
  const shared = () => {
    if (!notice?.scopeClassified || !notice.sharedToAll) return false;
    const ids = new Set(notice.layers.flatMap(layer => 'mediaId' in layer ? [layer.mediaId] : []));
    return [...ids].every(id => mediaList.some(item => item.id === id && item.scopeClassified && item.sharedToAll));
  };
  const [sharedReady, setSharedReady] = useState(shared);
  const noticeId = notice?.id ?? null;
  const orientation = portrait ? 'portrait' : 'landscape';
  const request = async (path: string, body?: unknown) => {
    const response = await fetch(`${API_BASE}/api/group-workflow/${path}`, { credentials: 'include',
      ...(body === undefined ? {} : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }) });
    if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.error || '설정을 처리하지 못했습니다.'); }
    return response.status === 204 ? null : response.json();
  };
  useEffect(() => {
    if (!isBo) return;
    let active = true;
    void Promise.all([api.getOrg(), api.boListTenants()]).then(([current, tenants]) => {
      if (!active) return;
      const available = tenants.filter(item => item.status === 'active');
      setOrganizations(available);
      setCurrentOrgId(current.id);
      setTargetOrgId(value => value || (available.some(item => item.id === current.id) ? current.id : available[0]?.id ?? ''));
    }).catch(() => { if (active) setMessage('조직 목록을 불러오지 못했습니다.'); });
    return () => { active = false; };
  }, [isBo]);
  useEffect(() => {
    let active = true;
    const query = new URLSearchParams({ orientation, ...(targetOrgId ? { orgId: targetOrgId } : {}) });
    void request(`idle-content?${query}`).then(data => {
      if (!active) return;
      setMessage(data.notice ? `현재 게시: ${data.notice.name}` : '현재 게시: APK 기본 대기 화면');
    }).catch(error => { if (active) setMessage(error.message); });
    return () => { active = false; };
  }, [orientation, targetOrgId]);
  const action = async (run: () => Promise<unknown>, success: string) => {
    setBusy(true); try { await run(); setMessage(success); } catch (error) { setMessage(error instanceof Error ? error.message : '처리 실패'); } finally { setBusy(false); }
  };
  return <ModalShell title="기본 대기 화면" onClose={onClose} panelClassName="w-[540px] max-h-[85vh] overflow-auto">
    <div className="space-y-4 text-[13px]">
      <p>조직 공통 · {portrait ? '세로' : '가로'} 화면. 현재 슬라이드를 조직 내 공유로 저장한 뒤 게시하면 기기에 자동 반영됩니다.</p>
      {/* 시계·날씨 대기 템플릿은 상단 '템플릿' 갤러리로 옮겼다(2026-09-16 대교). 여기는 게시만. */}
      {isBo && <label className="block space-y-1"><span className="font-semibold">적용 조직</span>
        <select aria-label="대기 화면 적용 조직" value={targetOrgId} disabled={busy || !organizations.length}
          onChange={event => { const value = event.target.value; setTargetOrgId(value); setTargetNoticeId(null); setSharedReady(value === currentOrgId && shared()); }}
          className="w-full rounded-md border border-line bg-panel-2 px-3 py-2.5 text-text focus:border-accent focus:outline-none">
          {organizations.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
        <span className="block text-[12px] text-text-dim">다른 조직을 선택하면 슬라이드와 미디어를 해당 조직 소유 사본으로 복제합니다.</span>
      </label>}
      <section className="rounded-lg border border-line bg-panel-2 p-3">
        <div className="flex items-start justify-between gap-3">
          <div><p className="font-semibold">조직 내 공유</p><p className="mt-1 text-[12px] text-text-dim">현재 슬라이드와 사용 중인 미디어를 이 조직의 대기 화면으로 사용할 수 있게 공유합니다.</p></div>
          <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${sharedReady ? 'bg-green-500/15 text-green-600' : 'bg-amber-400/20 text-amber-700'}`}>{sharedReady ? '공유 완료' : '공유 필요'}</span>
        </div>
        {isBo ? <Button className="mt-3" disabled={busy || !noticeId || sharedReady} onClick={() => action(async () => {
          const result = await request('idle-content/share', { noticeId, targetOrgId: targetOrgId || undefined }) as { noticeId: string };
          setTargetNoticeId(result.noticeId); setSharedReady(true); return result;
        }, '슬라이드와 사용 미디어를 조직 내 공유로 설정했습니다.')}>{sharedReady ? '조직 내 공유 완료' : '슬라이드·미디어 조직 내 공유'}</Button>
          : !sharedReady && <p className="mt-2 text-[12px] text-text-dim">최고 관리자 (H9) 계정에서 조직 내 공유를 설정해 주세요.</p>}
      </section>
      <div className="flex gap-2">
        <Button variant="primary" disabled={busy || !noticeId || !canPublish || !sharedReady} onClick={() => action(() => request('idle-content', { orientation, noticeId: targetNoticeId ?? noticeId, targetOrgId: targetOrgId || undefined }), '게시가 완료되었습니다. 선택한 조직의 기기가 새 대기 화면을 자동으로 다운로드합니다.')}>대기 화면 게시</Button>
        <Button disabled={busy} onClick={() => action(() => request('idle-content', { orientation, noticeId: null, targetOrgId: targetOrgId || undefined }), '선택한 조직을 APK 기본 대기 화면으로 복원했습니다.')}>기본 화면 복원</Button>
      </div>
      {!canPublish && <p>현재 편집 내용을 먼저 저장하고 다시 열어야 게시할 수 있습니다.</p>}
      {/* 설치 지역(조직 기본)은 날씨 위젯 설정 > '설치 지역 사용'으로 옮겼다(2026-09-16 대교). */}
      <p role="status">{message}</p>
      <p className="text-text-dim">게시에는 중앙 승인 권한이 필요합니다. 편성이나 일반 슬라이드 사본은 변경되지 않습니다.</p>
    </div>
  </ModalShell>;
}
